/**
 * Adapter to integrate comprehensive familiarisation items with existing UI
 * Maps new structure from regulations assistant to existing checklist format
 */

import { 
  getFamiliarisationItems,
  FAMILIARISATION_SECTIONS
} from '../familiarisationItems5Sections';

import { transformVesselConfig } from '../vesselConfigMapping';

/**
 * Convert new item format to existing checklist format
 * @param {Object} newItem - Item from familiarisationItemsComplete
 * @param {Object} vessel - Vessel configuration
 * @param {Object} crew - Crew member
 * @returns {Object} Item in existing checklist format
 */
function convertItemFormat(newItem, vessel, crew) {
  // The role filtering is already done by getFamiliarisationItems
  // so we don't need to check roles here
  
  return {
    id: newItem.id,
    text: newItem.item,
    description: newItem.description || '',
    reference: newItem.wbc3Ref || 'WBC3 Compliance',
    priority: newItem.priority || 'normal',
    classification: newItem.classification,
    role: newItem.role,
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: [
      'Not applicable to vessel type',
      'Equipment not fitted',
      'Operation not conducted',
      'Alternative arrangements in place'
    ],
    tooltip: `${newItem.wbc3Ref} - ${newItem.item}`,
    // Preserve role information for display
    applicableRoles: newItem.roles
  };
}

/**
 * Get items for a specific section with conditional logic applied
 * @param {string} sectionId - Section identifier
 * @param {Object} vessel - Vessel configuration
 * @param {Object} crew - Crew member
 * @param {Object} allItemsBySection - All items organized by section
 * @returns {Array} Filtered items for the section
 */
export function getSectionItems(sectionId, vessel, crew, allItemsBySection) {
  const items = [];
  
  // Get items for this section
  const sectionItems = allItemsBySection[sectionId] || [];
  
  // Convert each item to the existing format
  sectionItems.forEach(item => {
    const converted = convertItemFormat(item, vessel, crew);
    if (converted) {
      items.push(converted);
    }
  });
  
  return items;
}

/**
 * Generate complete familiarisation checklist using new comprehensive items
 * @param {Object} vessel - Vessel configuration (already transformed by transformVesselConfig)
 * @param {Object} crew - Crew member
 * @returns {Object} Complete familiarisation structure
 */
export function generateComprehensiveFamiliarisation(vessel, crew) {
  // Determine crew role
  const crewRole = crew.position?.toLowerCase().includes('master') ? 'master' :
                   crew.position?.toLowerCase().includes('engineer') ? 'engineer' : 
                   'crew';
  
  // Get all familiarisation items for this vessel and crew role
  const itemsBySection = getFamiliarisationItems(vessel, crewRole);
  
  // Build sections array
  const sections = [];
  
  Object.entries(FAMILIARISATION_SECTIONS).forEach(([sectionId, sectionDef]) => {
    const sectionItems = getSectionItems(sectionId, vessel, crew, itemsBySection);
    
    if (sectionItems.length > 0) {
      sections.push({
        section: sectionId,
        title: sectionDef.title,
        description: sectionDef.description,
        reference: 'WBC3 Compliance',
        mandatory: true,
        items: sectionItems
      });
    }
  });
  
  return {
    vessel: vessel,
    crew: crew,
    sections: sections
  };
}